import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../../../context/AuthContext';
import { listMembers, addMember, removeMember, searchAssociates,
  listGateApprovers, setGateApprover, listReviewDelegations, setReviewDelegation } from './api';

const GATE_LABELS = { development: 'Approve for development', publication: 'Approve for publication' };
const REVIEW_LABELS = { source: 'Source corroboration review', executive: 'Executive review' };

const DELEG_SEL = 'text-sm px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800';

function ApprovalDelegation() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['training-gate-approvers'], queryFn: listGateApprovers });
  const { data: memberData } = useQuery({ queryKey: ['training-members'], queryFn: listMembers });
  const members = memberData?.members || [];
  const setRole = useMutation({
    mutationFn: ({ gate, body }) => setGateApprover(gate, body),
    onSuccess: () => { toast.success('Approver updated'); qc.invalidateQueries({ queryKey: ['training-gate-approvers'] }); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });
  const roles = data?.roles || [];
  return (
    <div className="mt-8">
      <h3 className="text-sm font-semibold mb-1">Approval delegation</h3>
      <p className="text-xs text-gray-500 mb-3">
        A Director or admin can always approve these gates. Delegate a gate to a role (any holder can approve),
        or narrow it to one specific person.
      </p>
      <div className="space-y-2 max-w-[640px]">
        {(data?.gates || []).map((g) => {
          const roleMembers = members.filter((m) => m.training_role === g.role);
          return (
            <div key={g.gate} className="flex items-center justify-between gap-2 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2">
              <span className="text-sm">{GATE_LABELS[g.gate] || g.gate}</span>
              <div className="flex items-center gap-2">
                <select value={g.role} onChange={(e) => setRole.mutate({ gate: g.gate, body: { training_role: e.target.value, user_id: '' } })} className={DELEG_SEL}>
                  {roles.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                </select>
                <select value={g.user?.id || ''} onChange={(e) => setRole.mutate({ gate: g.gate, body: { user_id: e.target.value } })} className={DELEG_SEL}>
                  <option value="">Anyone with role</option>
                  {roleMembers.map((m) => <option key={m.user_public_id} value={m.user_public_id}>{m.name}</option>)}
                </select>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewDelegation() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['training-review-delegations'], queryFn: listReviewDelegations });
  const { data: memberData } = useQuery({ queryKey: ['training-members'], queryFn: listMembers });
  const members = memberData?.members || [];
  const setRole = useMutation({
    mutationFn: ({ reviewType, body }) => setReviewDelegation(reviewType, body),
    onSuccess: () => { toast.success('Delegation updated'); qc.invalidateQueries({ queryKey: ['training-review-delegations'] }); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });
  const roles = data?.roles || [];
  return (
    <div className="mt-8">
      <h3 className="text-sm font-semibold mb-1">Review delegation</h3>
      <p className="text-xs text-gray-500 mb-3">
        Source corroboration and executive reviews have no dedicated role, so a Director owns them by default.
        Delegate one to a role, or narrow it to one specific person, who can then be assigned it and decide it
        (a Director or admin always can).
      </p>
      <div className="space-y-2 max-w-[640px]">
        {(data?.review_types || []).map((rt) => {
          const roleMembers = members.filter((m) => m.training_role === rt.role);
          return (
            <div key={rt.review_type} className="flex items-center justify-between gap-2 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2">
              <span className="text-sm">{REVIEW_LABELS[rt.review_type] || rt.review_type}</span>
              <div className="flex items-center gap-2">
                <select value={rt.role} onChange={(e) => setRole.mutate({ reviewType: rt.review_type, body: { training_role: e.target.value, user_id: '' } })} className={DELEG_SEL}>
                  {roles.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                </select>
                <select value={rt.user?.id || ''} onChange={(e) => setRole.mutate({ reviewType: rt.review_type, body: { user_id: e.target.value } })} className={DELEG_SEL}>
                  <option value="">Anyone with role</option>
                  {roleMembers.map((m) => <option key={m.user_public_id} value={m.user_public_id}>{m.name}</option>)}
                </select>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ROLES = [
  ['curriculum_developer', 'Curriculum Developer'],
  ['learning_experience_designer', 'Learning Experience Designer'],
  ['trainer', 'Trainer'],
  ['director', 'Director (admin only)'],
];

export default function TeamRoles() {
  const qc = useQueryClient();
  const { currentUser } = useAuth();
  const isAdmin = !!currentUser?.admin;
  const [q, setQ] = useState('');
  const [role, setRole] = useState('curriculum_developer');
  const [picked, setPicked] = useState(null);

  const { data } = useQuery({ queryKey: ['training-members'], queryFn: listMembers });
  const { data: search } = useQuery({
    queryKey: ['training-assoc', q],
    queryFn: () => searchAssociates(q),
    enabled: q.length >= 2,
  });

  const add = useMutation({
    mutationFn: () => addMember(picked.id, role),
    onSuccess: () => {
      toast.success('Member added');
      setPicked(null);
      setQ('');
      qc.invalidateQueries({ queryKey: ['training-members'] });
    },
    onError: (e) => toast.error(e?.message || 'Not permitted'),
  });
  const remove = useMutation({
    mutationFn: (id) => removeMember(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['training-members'] }),
    onError: (e) => toast.error(e?.message || 'Not permitted'),
  });

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <h2 className="text-lg font-semibold">Team &amp; Roles</h2>
        <span className="text-[10px] bg-gray-900 text-orange-400 px-2 py-0.5 rounded-full">Director / Admin only</span>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Only internal staff are eligible. A global admin designates the Director; the Director designates everyone else.
      </p>

      <div className="flex flex-wrap items-center gap-2 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl p-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <input
            value={picked ? `${picked.name} (${picked.email})` : q}
            onChange={(e) => {
              setPicked(null);
              setQ(e.target.value);
            }}
            placeholder="Search internal associates…"
            className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent"
          />
          {!picked && search?.associates?.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow">
              {search.associates.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setPicked(a)}
                  className="block w-full text-left text-sm px-3 py-2 hover:bg-blue-50 dark:hover:bg-gray-700"
                >
                  {a.name} <span className="text-gray-400">· {a.role}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800"
        >
          {ROLES.map(([v, l]) => (
            <option key={v} value={v} disabled={v === 'director' && !isAdmin}>
              {l}
            </option>
          ))}
        </select>
        <button
          disabled={!picked || add.isPending}
          onClick={() => add.mutate()}
          className="text-sm font-bold px-4 py-2 rounded-lg bg-gray-900 text-white dark:bg-gray-200 dark:text-gray-900 hover:bg-orange-600 disabled:opacity-40"
        >
          + Add member
        </button>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <th className="py-2 px-2">Member</th>
            <th className="py-2 px-2">Role</th>
            <th className="py-2 px-2">Assigned by</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data?.members?.map((m) => {
            // A director row is editable/removable by a global admin only.
            const editable = !m.locked || isAdmin;
            return (
              <tr key={m.id} className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-3 px-2">
                  {m.name}
                  <div className="text-xs text-gray-400">{m.email}</div>
                </td>
                <td className="py-3 px-2">
                  {m.training_role.replace(/_/g, ' ')}
                  {m.locked && <span className="ml-2 text-[10px] text-gray-400">🔒 admin-only</span>}
                </td>
                <td className="py-3 px-2 text-gray-500">{m.assigned_by || '-'}</td>
                <td className="py-3 px-2 text-right">
                  {editable && (
                    <button onClick={() => remove.mutate(m.id)} className="text-xs text-red-500">
                      remove
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <ApprovalDelegation />
      <ReviewDelegation />
    </div>
  );
}
